import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Card,
  CardMedia,
  CardContent,
} from "@mui/material";
import {
  Link as LinkIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
} from "@mui/icons-material";

const YouTubeEmbed = ({
  value,
  onChange,
  disabled = false,
  error,
  helperText,
  classes,
  form,
  setForm,
}) => {
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [videoInfo, setVideoInfo] = useState(null);

  // Extract YouTube video ID from URL
  const extractVideoId = (url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Validate YouTube URL
  const validateYouTubeUrl = (url) => {
    const videoId = extractVideoId(url);
    return videoId !== null;
  };

  // Get video thumbnail and title
  const getVideoInfo = async (videoId) => {
    try {
      // Using YouTube oEmbed API (no API key required)
      const response = await fetch(
        `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`
      );

      if (response.ok) {
        const data = await response.json();
        return {
          id: videoId,
          title: data.title,
          thumbnail: data.thumbnail_url,
          author: data.author_name,
          embedUrl: `https://www.youtube.com/embed/${videoId}`,
        };
      }
    } catch (error) {
      console.error("Error fetching video info:", error);
    }
    return null;
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setYoutubeUrl(url);
    setUrlError("");

    if (url && !validateYouTubeUrl(url)) {
      setUrlError("Please enter a valid YouTube URL");
    }
  };

  // Debounced auto-preview
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (youtubeUrl && validateYouTubeUrl(youtubeUrl)) {
        handleEmbedVideo();
      }
    }, 1000); // 1 giây delay

    return () => clearTimeout(timeoutId);
  }, [youtubeUrl]);

  const handleEmbedVideo = async () => {
    if (!youtubeUrl || !validateYouTubeUrl(youtubeUrl)) {
      setUrlError("Please enter a valid YouTube URL");
      return;
    }

    const videoId = extractVideoId(youtubeUrl);
    const info = await getVideoInfo(videoId);

    if (info) {
      setVideoInfo(info);

      const videoData = {
        type: "youtube",
        videoId: info.id,
        title: info.title,
        thumbnail: info.thumbnail,
        author: info.author,
        embedUrl: info.embedUrl,
        originalUrl: youtubeUrl,
      };

      if (onChange) {
        onChange(videoData);
      }

      if (form && setForm) {
        setForm({
          ...form,
          video: videoData,
        });
      }
    } else {
      setUrlError("Could not fetch video information");
    }
  };

  const handleDelete = () => {
    setVideoInfo(null);
    setYoutubeUrl("");
    setUrlError("");

    if (onChange) {
      onChange(null);
    }

    if (form && setForm) {
      setForm({
        ...form,
        video: null,
      });
    }
  };

  // Initialize from value prop
  useEffect(() => {
    if (value && value.originalUrl) {
      setYoutubeUrl(value.originalUrl || "");
      setVideoInfo({
        id: value.videoId,
        title: value.title,
        thumbnail: value.thumbnail,
        author: value.author,
        embedUrl: value.embedUrl,
      });
    }
  }, [value]);

  return (
    <Box className={classes}>
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <TextField
          fullWidth
          label="YouTube URL"
          placeholder="https://www.youtube.com/watch?v=..."
          value={youtubeUrl}
          onChange={handleUrlChange}
          disabled={disabled}
          error={!!urlError}
          helperText={urlError}
          InputProps={{
            startAdornment: (
              <LinkIcon sx={{ mr: 1, color: "text.secondary" }} />
            ),
          }}
        />
      </Box>

      {videoInfo && (
        <Card sx={{ position: "relative" }}>
          <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
            <IconButton
              size="small"
              onClick={handleDelete}
              disabled={disabled}
              color="error"
              sx={{ bgcolor: "rgba(255,255,255,0.8)" }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>

          <Box sx={{ position: "relative" }}>
            <CardMedia
              component="img"
              height="200"
              image={videoInfo.thumbnail}
              alt={videoInfo.title}
            />
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "rgba(0,0,0,0.7)",
                borderRadius: "50%",
                p: 1,
                cursor: "pointer",
              }}
              onClick={() => window.open(videoInfo.embedUrl, "_blank")}
            >
              <PlayIcon sx={{ color: "white", fontSize: 40 }} />
            </Box>
          </Box>

          <CardContent>
            <Typography variant="h6" component="div" noWrap>
              {videoInfo.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              by {videoInfo.author}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              YouTube Video ID: {videoInfo.id}
            </Typography>
          </CardContent>
        </Card>
      )}

      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          {helperText}
        </Typography>
      )}

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

YouTubeEmbed.propTypes = {
  value: PropTypes.shape({
    originalUrl: PropTypes.string,
    videoId: PropTypes.string,
    title: PropTypes.string,
    thumbnail: PropTypes.string,
    author: PropTypes.string,
    embedUrl: PropTypes.string,
  }),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  helperText: PropTypes.string,
  classes: PropTypes.string,
  form: PropTypes.object,
  setForm: PropTypes.func,
};

export default YouTubeEmbed;
